import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../../app/store/utils/baseQueryWithReauth';
import type {
  LegalPageChannelScope,
  LegalPageEditorStateDto,
  LegalPagePublishStateDto,
  LegalPageSlug,
  SaveLegalPageDraftDto,
  LegalPageLocale,
} from '../../application/dto/LegalPage.dto';

function unwrapData<T>(response: unknown): T {
  if (
    response != null &&
    typeof response === 'object' &&
    'data' in response &&
    (response as { data?: T }).data != null
  ) {
    return (response as { data: T }).data;
  }
  return response as T;
}

function channelLegalPagesBase(scope: LegalPageChannelScope, slug: LegalPageSlug): string {
  const { companyId, stockOwnerId, channelId } = scope;
  return `/system/companies/${companyId}/stock-owners/${stockOwnerId}/channels/${channelId}/legal-pages/${slug}`;
}

function scopeTagId(scope: LegalPageChannelScope, slug: LegalPageSlug): string {
  return `${scope.companyId}-${scope.stockOwnerId}-${scope.channelId}-${slug}`;
}

export const legalPagesApi = createApi({
  reducerPath: 'legalPagesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['LegalPageEditor', 'LegalPagePublishState'],
  endpoints: build => ({
    getLegalPageEditorState: build.query<
      LegalPageEditorStateDto,
      { scope: LegalPageChannelScope; slug: LegalPageSlug }
    >({
      query: ({ scope, slug }) => ({
        url: channelLegalPagesBase(scope, slug),
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        unwrapData<LegalPageEditorStateDto>(response),
      providesTags: (_r, _e, { scope, slug }) => [
        { type: 'LegalPageEditor', id: scopeTagId(scope, slug) },
      ],
    }),
    getLegalPagePublishState: build.query<
      LegalPagePublishStateDto,
      { scope: LegalPageChannelScope; slug: LegalPageSlug }
    >({
      query: ({ scope, slug }) => ({
        url: `${channelLegalPagesBase(scope, slug)}/publish-state`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        unwrapData<LegalPagePublishStateDto>(response),
      providesTags: (_r, _e, { scope, slug }) => [
        { type: 'LegalPagePublishState', id: scopeTagId(scope, slug) },
      ],
    }),
    saveLegalPageDraft: build.mutation<
      void,
      { scope: LegalPageChannelScope; slug: LegalPageSlug; body: SaveLegalPageDraftDto }
    >({
      query: ({ scope, slug, body }) => ({
        url: `${channelLegalPagesBase(scope, slug)}/draft`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { scope, slug }) => [
        { type: 'LegalPageEditor', id: scopeTagId(scope, slug) },
        { type: 'LegalPagePublishState', id: scopeTagId(scope, slug) },
      ],
    }),
    publishLegalPage: build.mutation<
      void,
      { scope: LegalPageChannelScope; slug: LegalPageSlug; locale?: LegalPageLocale }
    >({
      query: ({ scope, slug, locale }) => ({
        url: `${channelLegalPagesBase(scope, slug)}/publish`,
        method: 'POST',
        params: locale ? { locale } : undefined,
      }),
      invalidatesTags: (_r, _e, { scope, slug }) => [
        { type: 'LegalPageEditor', id: scopeTagId(scope, slug) },
        { type: 'LegalPagePublishState', id: scopeTagId(scope, slug) },
      ],
    }),
  }),
});

export const {
  useGetLegalPageEditorStateQuery,
  useGetLegalPagePublishStateQuery,
  useSaveLegalPageDraftMutation,
  usePublishLegalPageMutation,
} = legalPagesApi;
